import type { ReactNode } from "react";
import { cn } from "@marketing-sites/utils/cn";
import { renderDrlpCta, type DrlpCta } from "../cta";

/**
 * Internal DRLP composition helper — the label / headline / description / CTA
 * block repeated at the top of most DRLP sections. It is NOT a DRLP section and
 * is intentionally not re-exported from the package barrel: sections import it by
 * relative path and forward their own authored fields, so editors keep
 * configuring the same Sanity fields on each parent section.
 */
type DrlpSectionHeaderCta = DrlpCta;

interface DrlpSectionHeaderProps {
  title: ReactNode;
  /** Eyebrow label above the heading. Omit to hide. */
  badge?: string;
  /** Body copy. Pass an array to render multiple stacked paragraphs. Omit to hide. */
  description?: string | string[];
  /** Primary (solid) CTA. Omit to hide. */
  primaryCta?: DrlpSectionHeaderCta;
  /** Secondary (outline) CTA. Omit to hide. */
  secondaryCta?: DrlpSectionHeaderCta;
  /**
   * Header layout at desktop. "horizontal" places the description + CTAs beside
   * the title (title left, body right) to reduce the header's vertical height;
   * "vertical" (default) stacks them. Both stack on mobile.
   */
  align?: "vertical" | "horizontal";
  /** Override the wrapper classes (defaults to the standard grid column span). */
  className?: string;
}

export default function DrlpSectionHeader({
  title,
  badge,
  description,
  primaryCta,
  secondaryCta,
  align = "vertical",
  className,
}: DrlpSectionHeaderProps) {
  const paragraphs =
    description == null
      ? []
      : Array.isArray(description)
        ? description
        : [description];
  const heading = (
    <>
      {badge && (
        <span className="text-label-lg mb-6 inline-block">{badge}</span>
      )}
      {title && <h2 className="text-heading-3 text-balance">{title}</h2>}
    </>
  );
  const hasBody =
    paragraphs.length > 0 || Boolean(primaryCta) || Boolean(secondaryCta);
  const body = (
    <>
      {paragraphs.length > 0 && (
        <div className="text-body-lg max-w-[55ch] space-y-4 text-balance">
          {paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      )}
      {(primaryCta || secondaryCta) && (
        <div className="flex flex-wrap items-center gap-2">
          {renderDrlpCta(primaryCta, "primary")}
          {renderDrlpCta(secondaryCta, "secondary")}
        </div>
      )}
    </>
  );

  if (align === "horizontal") {
    // Label sits on its own full-width row so it never shifts the title; the
    // title and body then share a row (tops aligned), with the body nudged 12px
    // below the title at desktop per the design spec.
    return (
      <div
        data-testid="drlp-section-header"
        className={cn(
          "col-span-full grid grid-cols-subgrid gap-y-6",
          className,
        )}
      >
        {badge && (
          <span className="text-label-lg col-span-full inline-block">
            {badge}
          </span>
        )}
        {title && (
          <h2 className="text-heading-3 col-span-full text-balance lg:col-span-6">
            {title}
          </h2>
        )}
        {hasBody && (
          <div className="col-span-full space-y-6 lg:col-span-4 lg:col-start-7 lg:pt-3">
            {body}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="drlp-section-header"
      className={cn("col-span-full space-y-6 lg:col-span-6", className)}
    >
      {heading}
      {body}
    </div>
  );
}
